import { type ReactNode, useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatBRL } from "@/lib/format";

import { RotuloCobertura, useCatalogoCoberturas } from "./comuns";
import {
  arquivoEmBase64,
  type ApoliceDetalhe,
  consultarPortal,
  executarNoPortal,
  NF_MAX_BYTES,
  NF_TIPOS,
  ROTULOS_ACAO,
  SITUACOES_LEAD,
  type TipoAcao,
} from "./portalAzosAcoes";

/**
 * Confirmação de toda ação que grava no portal Azos. `montar` devolve os dados
 * ou uma frase de erro de preenchimento; nada sai antes do clique em Enviar.
 */
export const ConfirmarAcao = ({
  tipo,
  aberto,
  aoFechar,
  aoConcluir,
  descricao,
  montar,
  children,
}: {
  tipo: TipoAcao;
  aberto: boolean;
  aoFechar: () => void;
  aoConcluir?: (url?: string) => void;
  descricao: ReactNode;
  montar: () => Promise<Record<string, unknown> | string> | Record<string, unknown> | string;
  children?: ReactNode;
}) => {
  const notify = useNotify();
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto) setErro(null);
  }, [aberto]);

  const enviar = async () => {
    setErro(null);
    setEnviando(true);
    try {
      const dados = await montar();
      if (typeof dados === "string") {
        setErro(dados);
        return;
      }
      const { dados: r, erro: falha } = await executarNoPortal(tipo, dados);
      if (falha) {
        setErro(falha);
        return;
      }
      notify(`${ROTULOS_ACAO[tipo]}: enviado ao portal da Azos.`);
      aoConcluir?.(r?.url);
      aoFechar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && !enviando && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{ROTULOS_ACAO[tipo]}</DialogTitle>
          <DialogDescription>{descricao}</DialogDescription>
        </DialogHeader>
        {children}
        {erro && <p className="text-sm text-destructive">{erro}</p>}
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button type="button" onClick={enviar} disabled={enviando}>
            {enviando ? "Enviando..." : "Enviar ao portal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Nota fiscal de um recebimento: arquivo ou só a informação de que foi emitida. */
export const DialogoNotaFiscal = ({
  recebimentoId,
  descricao,
  aberto,
  aoFechar,
  aoConcluir,
}: {
  recebimentoId: string;
  descricao: string;
  aberto: boolean;
  aoFechar: () => void;
  aoConcluir: () => void;
}) => {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [semArquivo, setSemArquivo] = useState(false);
  useEffect(() => {
    if (aberto) {
      setArquivo(null);
      setSemArquivo(false);
    }
  }, [aberto]);

  return (
    <ConfirmarAcao
      tipo={semArquivo ? "nf_informada" : "nf_enviar"}
      aberto={aberto}
      aoFechar={aoFechar}
      aoConcluir={aoConcluir}
      descricao={descricao}
      montar={async () => {
        if (semArquivo) return { recebimento_id: recebimentoId };
        if (!arquivo) return "Selecione o arquivo da nota fiscal.";
        if (!NF_TIPOS.includes(arquivo.type)) return "Nota fiscal em PDF, XML, PNG ou JPG.";
        if (arquivo.size > NF_MAX_BYTES) return "Arquivo acima de 5 MB.";
        return {
          recebimento_id: recebimentoId,
          arquivo_nome: arquivo.name,
          arquivo_tipo: arquivo.type,
          arquivo_base64: await arquivoEmBase64(arquivo),
        };
      }}
    >
      <div className="space-y-3">
        {!semArquivo && (
          <div className="space-y-1.5">
            <Label htmlFor="nf-arquivo">Arquivo (PDF, XML, PNG ou JPG, até 5 MB)</Label>
            <Input
              id="nf-arquivo"
              type="file"
              accept=".pdf,.xml,.png,.jpg,.jpeg"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={semArquivo} onChange={(e) => setSemArquivo(e.target.checked)} />
          Nota já emitida e entregue por outro canal (sem arquivo)
        </label>
      </div>
    </ConfirmarAcao>
  );
};

/** Pix ou boleto; cartão vai pelo link de cartão. */
export const DialogoPagamento = ({
  tipo,
  dadosBase,
  descricao,
  aberto,
  aoFechar,
  aoConcluir,
}: {
  tipo: "proposta_pagamento" | "apolice_pagamento";
  dadosBase: Record<string, unknown>;
  descricao: string;
  aberto: boolean;
  aoFechar: () => void;
  aoConcluir?: () => void;
}) => {
  const [metodo, setMetodo] = useState("pix");
  return (
    <ConfirmarAcao
      tipo={tipo}
      aberto={aberto}
      aoFechar={aoFechar}
      aoConcluir={aoConcluir}
      descricao={descricao}
      montar={() => ({ ...dadosBase, metodo })}
    >
      <div className="space-y-1.5">
        <Label>Forma de pagamento</Label>
        <Select value={metodo} onValueChange={setMetodo}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pix">Pix</SelectItem>
            <SelectItem value="bank_slip">Boleto</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </ConfirmarAcao>
  );
};

/** Vencimento: o portal oferece uma data; a confirmação aplica essa data. */
export const DialogoVencimento = ({
  propostaId,
  descricao,
  aberto,
  aoFechar,
  aoConcluir,
}: {
  propostaId: string;
  descricao: string;
  aberto: boolean;
  aoFechar: () => void;
  aoConcluir?: () => void;
}) => {
  const [oferta, setOferta] = useState<{ data: string | null; texto: string | null } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    if (!aberto) return;
    setOferta(null);
    setErro(null);
    consultarPortal<{ data: string | null; texto: string | null }>("vencimentos", propostaId)
      .then(setOferta)
      .catch((e) => setErro((e as Error).message));
  }, [aberto, propostaId]);

  return (
    <ConfirmarAcao
      tipo="proposta_vencimento"
      aberto={aberto}
      aoFechar={aoFechar}
      aoConcluir={aoConcluir}
      descricao={descricao}
      montar={() => (oferta?.data ? { proposta_id: propostaId, vencimento: oferta.data } : "Nenhuma data oferecida pelo portal.")}
    >
      <p className="text-sm">
        {erro ?? (oferta ? `Novo vencimento oferecido: ${oferta.texto ?? oferta.data ?? "—"}` : "Consultando o portal...")}
      </p>
    </ConfirmarAcao>
  );
};

/** Link para o segurado cadastrar o cartão; o portal também avisa o segurado. */
export const DialogoLinkCartao = ({
  propostaId,
  descricao,
  aberto,
  aoFechar,
}: {
  propostaId: string;
  descricao: string;
  aberto: boolean;
  aoFechar: () => void;
}) => {
  const notify = useNotify();
  const [avisar, setAvisar] = useState(true);
  const [link, setLink] = useState<string | null>(null);

  if (link) {
    return (
      <Dialog open={aberto} onOpenChange={(v) => !v && (setLink(null), aoFechar())}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Link de cartão de crédito</DialogTitle>
            <DialogDescription>{descricao}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={link} />
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigator.clipboard.writeText(link).then(() => notify("Link copiado."))}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => (setLink(null), aoFechar())}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <ConfirmarAcao
      tipo="proposta_link_cartao"
      aberto={aberto}
      aoFechar={aoFechar}
      aoConcluir={(url) => url && setLink(url)}
      descricao={descricao}
      montar={() => ({ proposta_id: propostaId, avisar_segurado: avisar })}
    >
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={avisar} onChange={(e) => setAvisar(e.target.checked)} />
        Aviso ao segurado pelo portal da Azos
      </label>
    </ConfirmarAcao>
  );
};

/** Endosso: novo capital por cobertura ou retirada; a Azos analisa em até 1 dia útil. */
export const DialogoEndosso = ({
  apoliceId,
  descricao,
  aberto,
  aoFechar,
}: {
  apoliceId: string;
  descricao: string;
  aberto: boolean;
  aoFechar: () => void;
}) => {
  const catalogo = useCatalogoCoberturas();
  const [apolice, setApolice] = useState<ApoliceDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [capitais, setCapitais] = useState<Record<string, string>>({});
  const [remover, setRemover] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!aberto) return;
    setApolice(null);
    setErro(null);
    setCapitais({});
    setRemover(new Set());
    consultarPortal<ApoliceDetalhe>("apolice", apoliceId)
      .then(setApolice)
      .catch((e) => setErro((e as Error).message));
  }, [aberto, apoliceId]);

  return (
    <ConfirmarAcao
      tipo="endosso"
      aberto={aberto}
      aoFechar={aoFechar}
      descricao={`${descricao} Análise da Azos em até 1 dia útil, com resposta por e-mail.`}
      montar={() => {
        if (!apolice) return "Apólice ainda não carregada.";
        const novos = Object.entries(capitais)
          .filter(([, v]) => v.trim() !== "")
          .map(([codigo, v]) => ({ codigo, valor: Number(v.replace(/\./g, "").replace(",", ".")) }));
        if (novos.some((n) => !Number.isFinite(n.valor) || n.valor <= 0)) return "Capital inválido.";
        return { apolice_id: apoliceId, capitais: novos, remover: [...remover] };
      }}
    >
      {erro && <p className="text-sm text-destructive">{erro}</p>}
      {!apolice && !erro && <p className="text-sm text-muted-foreground">Consultando o portal...</p>}
      {apolice && (
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {apolice.coberturas.map((c) => (
            <div key={c.codigo} className="grid grid-cols-[1fr_9rem_auto] items-center gap-2 rounded-md border p-2">
              <div className="text-sm">
                <RotuloCobertura seguradora="azos" codigo={c.codigo} catalogo={catalogo} />
                <div className="text-xs text-muted-foreground">
                  Capital {formatBRL(c.capital)} · prêmio {formatBRL(c.premio)}
                </div>
              </div>
              <Input
                inputMode="decimal"
                placeholder="Novo capital"
                disabled={remover.has(c.codigo)}
                value={capitais[c.codigo] ?? ""}
                onChange={(e) => setCapitais((v) => ({ ...v, [c.codigo]: e.target.value }))}
              />
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={remover.has(c.codigo)}
                  onChange={(e) =>
                    setRemover((s) => {
                      const n = new Set(s);
                      if (e.target.checked) n.add(c.codigo);
                      else n.delete(c.codigo);
                      return n;
                    })}
                />
                Retirar
              </label>
            </div>
          ))}
        </div>
      )}
    </ConfirmarAcao>
  );
};

/** Lead novo no portal: nome e telefone ou e-mail. */
export const DialogoNovoLead = ({ aberto, aoFechar, aoConcluir }: { aberto: boolean; aoFechar: () => void; aoConcluir: () => void }) => {
  const [campos, setCampos] = useState({ nome: "", telefone: "", email: "", nota: "" });
  useEffect(() => {
    if (aberto) setCampos({ nome: "", telefone: "", email: "", nota: "" });
  }, [aberto]);
  const campo = (k: keyof typeof campos, rotulo: string, tipo = "text") => (
    <div className="space-y-1.5">
      <Label htmlFor={`lead-${k}`}>{rotulo}</Label>
      <Input id={`lead-${k}`} type={tipo} value={campos[k]} onChange={(e) => setCampos((c) => ({ ...c, [k]: e.target.value }))} />
    </div>
  );
  return (
    <ConfirmarAcao
      tipo="lead_criar"
      aberto={aberto}
      aoFechar={aoFechar}
      aoConcluir={aoConcluir}
      descricao="Contato cadastrado na lista de leads do portal da Azos."
      montar={() => {
        if (!campos.nome.trim()) return "Informe o nome.";
        if (!campos.telefone.trim() && !campos.email.trim()) return "Informe telefone ou e-mail.";
        return campos;
      }}
    >
      <div className="grid gap-3">
        {campo("nome", "Nome")}
        <div className="grid gap-3 sm:grid-cols-2">
          {campo("telefone", "Telefone", "tel")}
          {campo("email", "E-mail", "email")}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lead-nota">Nota</Label>
          <Textarea id="lead-nota" rows={2} value={campos.nota} onChange={(e) => setCampos((c) => ({ ...c, nota: e.target.value }))} />
        </div>
      </div>
    </ConfirmarAcao>
  );
};

/** Situação ou nota de um lead do portal (uma ação por vez). */
export const DialogoLead = ({
  modo,
  leadId,
  nome,
  situacaoAtual,
  aberto,
  aoFechar,
  aoConcluir,
}: {
  modo: "situacao" | "nota";
  leadId: string;
  nome: string;
  situacaoAtual: string | null;
  aberto: boolean;
  aoFechar: () => void;
  aoConcluir: () => void;
}) => {
  const [situacao, setSituacao] = useState(situacaoAtual ?? "to_be_contacted");
  const [nota, setNota] = useState("");
  useEffect(() => {
    if (aberto) {
      setSituacao(situacaoAtual ?? "to_be_contacted");
      setNota("");
    }
  }, [aberto, situacaoAtual]);
  return (
    <ConfirmarAcao
      tipo={modo === "situacao" ? "lead_situacao" : "lead_nota"}
      aberto={aberto}
      aoFechar={aoFechar}
      aoConcluir={aoConcluir}
      descricao={`Lead ${nome}.`}
      montar={() => {
        if (modo === "nota") return nota.trim() ? { lead_id: leadId, nota } : "Informe a nota.";
        return situacao === situacaoAtual ? "Nenhuma alteração." : { lead_id: leadId, situacao };
      }}
    >
      {modo === "situacao" ? (
        <div className="space-y-1.5">
          <Label>Situação</Label>
          <Select value={situacao} onValueChange={setSituacao}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SITUACOES_LEAD).map(([v, r]) => (
                <SelectItem key={v} value={v}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="lead-nova-nota">Nota</Label>
          <Textarea id="lead-nova-nota" rows={3} value={nota} onChange={(e) => setNota(e.target.value)} />
        </div>
      )}
    </ConfirmarAcao>
  );
};
